<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useElementSize } from '@vueuse/core';
import Button from 'dashboard/components-next/button/Button.vue';
import {
  niceMax,
  linePath,
  gridValues,
  formatCount,
  formatDay,
  axisStride,
} from '../../helpers/operationsChartHelper';

const props = defineProps({
  days: {
    type: Array,
    default: () => [],
  },
});

const { t, locale } = useI18n();

const plotRef = ref(null);
const { width } = useElementSize(plotRef);
const showTable = ref(false);

const FLOW_HEIGHT = 252;
const BACKLOG_HEIGHT = 96;
const PADDING = { top: 14, right: 46, bottom: 26, left: 46 };

const chartWidth = computed(() => Math.max(width.value, 320));
const innerWidth = computed(
  () => chartWidth.value - PADDING.left - PADDING.right
);
const innerHeight = computed(() => FLOW_HEIGHT - PADDING.top - PADDING.bottom);

const x = index =>
  PADDING.left +
  (innerWidth.value * index) / Math.max(props.days.length - 1, 1);

const max = computed(() =>
  niceMax(
    Math.max(
      1,
      ...props.days.map(day =>
        Math.max(day.conversationsCount, day.resolutionsCount)
      )
    )
  )
);

const y = value =>
  PADDING.top + innerHeight.value - (innerHeight.value * value) / max.value;

const grid = computed(() =>
  gridValues(max.value).map(value => ({
    value: formatCount(Math.round(value), locale.value),
    y: y(value),
  }))
);

const createdPoints = computed(() =>
  props.days.map((day, index) => [x(index), y(day.conversationsCount)])
);
const resolvedPoints = computed(() =>
  props.days.map((day, index) => [x(index), y(day.resolutionsCount)])
);

const bandWidth = computed(
  () => innerWidth.value / Math.max(props.days.length - 1, 1)
);

const dayLabel = timestamp =>
  formatDay(timestamp, locale.value, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });

const axisLabels = computed(() =>
  props.days
    .map((day, index) => ({ day, index }))
    .filter(({ index }) => index % axisStride(props.days.length) === 0)
    .map(({ day, index }) => ({
      x: x(index),
      label: formatDay(day.timestamp, locale.value, {
        day: 'numeric',
        month: 'short',
      }),
    }))
);

const hoverBands = computed(() =>
  props.days.map((day, index) => ({
    key: day.timestamp,
    x: x(index) - bandWidth.value / 2,
    width: bandWidth.value,
    tip: [
      dayLabel(day.timestamp),
      `${t('OPERATIONS_REPORTS.FLOW.CREATED')}: ${formatCount(day.conversationsCount, locale.value)}`,
      `${t('OPERATIONS_REPORTS.FLOW.RESOLVED')}: ${formatCount(day.resolutionsCount, locale.value)}`,
    ].join('\n'),
  }))
);

// The backlog is a stock, not a flow, so it gets its own strip sharing the x axis instead of a
// second y axis on the plot above - two scales in one frame invent a correlation that is not there.
const backlogInnerHeight = BACKLOG_HEIGHT - 10 - 22;
const backlogBounds = computed(() => {
  const values = props.days.map(day => day.backlog);
  const high = Math.ceil(Math.max(...values, 1) / 25) * 25 + 25;
  const low = Math.max(0, Math.floor(Math.min(...values, 0) / 25) * 25 - 25);
  return { high, low };
});

const backlogY = value => {
  const { high, low } = backlogBounds.value;
  const span = high - low || 1;
  return 10 + backlogInnerHeight - (backlogInnerHeight * (value - low)) / span;
};

const backlogPoints = computed(() =>
  props.days.map((day, index) => [x(index), backlogY(day.backlog)])
);

const backlogHoverBands = computed(() =>
  props.days.map((day, index) => ({
    key: day.timestamp,
    x: x(index) - bandWidth.value / 2,
    width: bandWidth.value,
    tip: [
      dayLabel(day.timestamp),
      `${t('OPERATIONS_REPORTS.FLOW.BACKLOG')}: ${formatCount(day.backlog, locale.value)}`,
    ].join('\n'),
  }))
);

const lastDay = computed(() => props.days[props.days.length - 1]);

const tableRows = computed(() =>
  props.days.map(day => ({
    ...day,
    label: dayLabel(day.timestamp),
    balance: day.conversationsCount - day.resolutionsCount,
  }))
);
</script>

<template>
  <div
    class="flex flex-col p-4 border rounded-xl border-n-weak bg-n-solid-2 gap-1"
  >
    <div class="flex flex-wrap items-start justify-between gap-3">
      <div>
        <h3 class="text-sm font-semibold text-n-slate-12">
          {{ t('OPERATIONS_REPORTS.FLOW.TITLE') }}
        </h3>
        <p class="mt-0.5 mb-0 text-xs text-n-slate-11">
          {{ t('OPERATIONS_REPORTS.FLOW.SUBTITLE') }}
        </p>
      </div>
      <div class="flex flex-wrap items-center gap-4">
        <div class="flex items-center gap-3 text-xs text-n-slate-11">
          <span class="inline-flex items-center gap-1.5">
            <i class="w-3 h-[3px] rounded-sm bg-n-blue-9" />
            {{ t('OPERATIONS_REPORTS.FLOW.CREATED') }}
          </span>
          <span class="inline-flex items-center gap-1.5">
            <i class="w-3 h-[3px] rounded-sm bg-n-teal-9" />
            {{ t('OPERATIONS_REPORTS.FLOW.RESOLVED') }}
          </span>
        </div>
        <Button
          :label="
            showTable
              ? t('OPERATIONS_REPORTS.FLOW.HIDE_DATA')
              : t('OPERATIONS_REPORTS.FLOW.SHOW_DATA')
          "
          color="slate"
          variant="faded"
          size="xs"
          @click="showTable = !showTable"
        />
      </div>
    </div>

    <div ref="plotRef" class="w-full">
      <svg
        :viewBox="`0 0 ${chartWidth} ${FLOW_HEIGHT}`"
        :height="FLOW_HEIGHT"
        class="block w-full"
        role="img"
        :aria-label="t('OPERATIONS_REPORTS.FLOW.TITLE')"
      >
        <g v-for="line in grid" :key="`grid-${line.y}`">
          <line
            :x1="PADDING.left"
            :y1="line.y"
            :x2="PADDING.left + innerWidth"
            :y2="line.y"
            class="stroke-n-weak"
            stroke-width="1"
          />
          <text
            :x="PADDING.left - 8"
            :y="line.y + 4"
            text-anchor="end"
            font-size="10"
            class="fill-n-slate-10 tabular-nums"
          >
            {{ line.value }}
          </text>
        </g>

        <text
          v-for="label in axisLabels"
          :key="`axis-${label.x}`"
          :x="label.x"
          :y="FLOW_HEIGHT - 8"
          text-anchor="middle"
          font-size="10"
          class="fill-n-slate-10"
        >
          {{ label.label }}
        </text>

        <path
          :d="linePath(createdPoints)"
          fill="none"
          class="stroke-n-blue-9"
          stroke-width="2"
          stroke-linejoin="round"
          stroke-linecap="round"
        />
        <path
          :d="linePath(resolvedPoints)"
          fill="none"
          class="stroke-n-teal-9"
          stroke-width="2"
          stroke-linejoin="round"
          stroke-linecap="round"
        />

        <template v-if="lastDay">
          <circle
            :cx="x(days.length - 1)"
            :cy="y(lastDay.conversationsCount)"
            r="3.5"
            class="fill-n-blue-9 stroke-n-solid-2"
            stroke-width="2"
          />
          <circle
            :cx="x(days.length - 1)"
            :cy="y(lastDay.resolutionsCount)"
            r="3.5"
            class="fill-n-teal-9 stroke-n-solid-2"
            stroke-width="2"
          />
          <text
            :x="x(days.length - 1) + 8"
            :y="y(lastDay.conversationsCount) + 3"
            font-size="11"
            font-weight="600"
            class="fill-n-blue-9 tabular-nums"
          >
            {{ formatCount(lastDay.conversationsCount, locale) }}
          </text>
          <text
            :x="x(days.length - 1) + 8"
            :y="y(lastDay.resolutionsCount) + 3"
            font-size="11"
            font-weight="600"
            class="fill-n-teal-9 tabular-nums"
          >
            {{ formatCount(lastDay.resolutionsCount, locale) }}
          </text>
        </template>

        <rect
          v-for="band in hoverBands"
          :key="`hover-${band.key}`"
          :x="band.x"
          :y="PADDING.top"
          :width="band.width"
          :height="innerHeight"
          :data-tip="band.tip"
          class="fill-transparent hover:fill-n-alpha-1"
        />
      </svg>
    </div>

    <p class="pt-3 mt-2 mb-0 text-xs border-t text-n-slate-11 border-n-weak">
      {{ t('OPERATIONS_REPORTS.FLOW.BACKLOG_CAPTION') }}
    </p>

    <svg
      :viewBox="`0 0 ${chartWidth} ${BACKLOG_HEIGHT}`"
      :height="BACKLOG_HEIGHT"
      class="block w-full"
      role="img"
      :aria-label="t('OPERATIONS_REPORTS.FLOW.BACKLOG')"
    >
      <line
        :x1="PADDING.left"
        :y1="10"
        :x2="PADDING.left + innerWidth"
        :y2="10"
        class="stroke-n-weak"
        stroke-width="1"
      />
      <line
        :x1="PADDING.left"
        :y1="10 + backlogInnerHeight"
        :x2="PADDING.left + innerWidth"
        :y2="10 + backlogInnerHeight"
        class="stroke-n-weak"
        stroke-width="1"
      />
      <text
        :x="PADDING.left - 8"
        y="14"
        text-anchor="end"
        font-size="10"
        class="fill-n-slate-10 tabular-nums"
      >
        {{ formatCount(backlogBounds.high, locale) }}
      </text>
      <text
        :x="PADDING.left - 8"
        :y="10 + backlogInnerHeight + 4"
        text-anchor="end"
        font-size="10"
        class="fill-n-slate-10 tabular-nums"
      >
        {{ formatCount(backlogBounds.low, locale) }}
      </text>

      <path
        :d="linePath(backlogPoints)"
        fill="none"
        class="stroke-n-blue-8"
        stroke-width="2"
        stroke-linejoin="round"
      />
      <template v-if="lastDay">
        <circle
          :cx="x(days.length - 1)"
          :cy="backlogY(lastDay.backlog)"
          r="3.5"
          class="fill-n-blue-8 stroke-n-solid-2"
          stroke-width="2"
        />
        <text
          :x="x(days.length - 1) + 8"
          :y="backlogY(lastDay.backlog) + 3"
          font-size="11"
          font-weight="600"
          class="fill-n-slate-11 tabular-nums"
        >
          {{ formatCount(lastDay.backlog, locale) }}
        </text>
      </template>

      <rect
        v-for="band in backlogHoverBands"
        :key="`backlog-hover-${band.key}`"
        :x="band.x"
        y="10"
        :width="band.width"
        :height="backlogInnerHeight"
        :data-tip="band.tip"
        class="fill-transparent hover:fill-n-alpha-1"
      />
    </svg>

    <div v-if="showTable" class="mt-3 overflow-x-auto">
      <table class="w-full text-xs border-collapse">
        <thead>
          <tr class="text-n-slate-10">
            <th class="px-3 py-2 font-medium text-left uppercase">
              {{ t('OPERATIONS_REPORTS.FLOW.TABLE.DAY') }}
            </th>
            <th class="px-3 py-2 font-medium text-right uppercase">
              {{ t('OPERATIONS_REPORTS.FLOW.CREATED') }}
            </th>
            <th class="px-3 py-2 font-medium text-right uppercase">
              {{ t('OPERATIONS_REPORTS.FLOW.RESOLVED') }}
            </th>
            <th class="px-3 py-2 font-medium text-right uppercase">
              {{ t('OPERATIONS_REPORTS.FLOW.TABLE.BALANCE') }}
            </th>
            <th class="px-3 py-2 font-medium text-right uppercase">
              {{ t('OPERATIONS_REPORTS.FLOW.BACKLOG') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in tableRows"
            :key="row.timestamp"
            class="border-t border-n-weak"
          >
            <td class="px-3 py-1.5 text-n-slate-11">{{ row.label }}</td>
            <td class="px-3 py-1.5 text-right tabular-nums text-n-slate-12">
              {{ formatCount(row.conversationsCount, locale) }}
            </td>
            <td class="px-3 py-1.5 text-right tabular-nums text-n-slate-12">
              {{ formatCount(row.resolutionsCount, locale) }}
            </td>
            <td
              class="px-3 py-1.5 text-right tabular-nums"
              :class="row.balance > 0 ? 'text-n-ruby-11' : 'text-n-teal-11'"
            >
              {{ row.balance > 0 ? '+' : ''
              }}{{ formatCount(row.balance, locale) }}
            </td>
            <td class="px-3 py-1.5 text-right tabular-nums text-n-slate-12">
              {{ formatCount(row.backlog, locale) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
